import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api, Article, Tag } from '../utils/api';
import { ArticleCard, CardSize } from '../components/ArticleCard';
import { Pagination } from '../components/Pagination';
import { SEO } from '../components/SEO';
import { websiteSchema } from '../config/seo';

const CARD_SIZE_KEY = 'bytejourney-card-size';
const INITIAL_TAGS_SHOWN = 6;

export function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState<Article[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [untaggedCount, setUntaggedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cardSize, setCardSize] = useState<CardSize>(() => {
    const saved = localStorage.getItem(CARD_SIZE_KEY);
    return (saved as CardSize) || 'large';
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });
  const [showAllTags, setShowAllTags] = useState(false);

  const handleCardSizeChange = (size: CardSize) => {
    setCardSize(size);
    localStorage.setItem(CARD_SIZE_KEY, size);
  };

  const currentPage = parseInt(searchParams.get('page') || '1');
  const currentTag = searchParams.get('tag') || undefined;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [articlesRes, tagsRes] = await Promise.all([
          api.getArticles({ page: currentPage, limit: 10, tag: currentTag }),
          api.getTags(),
        ]);

        setArticles(articlesRes.articles);
        setPagination({
          page: articlesRes.pagination.page,
          totalPages: articlesRes.pagination.totalPages,
          total: articlesRes.pagination.total,
        });
        setTags(tagsRes.tags);
        setUntaggedCount(tagsRes.untaggedCount);
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, currentTag]);

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(page));
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTagClick = (tag: string | null) => {
    const params = new URLSearchParams();
    if (tag) {
      params.set('tag', tag);
    }
    setSearchParams(params);
  };

  return (
    <>
      <SEO canonical="/" ogType="website" schema={websiteSchema} />
      <h1 className="sr-only">ByteJourney - AI, SEO, and Emerging Tech Insights</h1>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Main content */}
        <div className="flex-1">
          {/* Header with filter and view toggle */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {currentTag && (
                <>
                  <span className="text-gray-600 dark:text-gray-400">Filtered by:</span>
                  <span className="badge-primary text-sm">
                    {currentTag === '__untagged__' ? 'Untagged' : `#${currentTag}`}
                  </span>
                  <button
                    onClick={() => handleTagClick(null)}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Clear filter
                  </button>
                </>
              )}
            </div>

            {/* Card size toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => handleCardSizeChange('large')}
                className={`p-1.5 rounded-md transition-colors ${
                  cardSize === 'large'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                title="Large cards"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                </svg>
              </button>
              <button
                onClick={() => handleCardSizeChange('compact')}
                className={`p-1.5 rounded-md transition-colors ${
                  cardSize === 'compact'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                title="Compact list"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner w-8 h-8" />
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-lg">No articles found</p>
              {currentTag && (
                <button
                  onClick={() => handleTagClick(null)}
                  className="mt-4 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  View all articles
                </button>
              )}
            </div>
          ) : (
            <>
              <div className={cardSize === 'large' ? 'grid gap-6 md:grid-cols-2' : 'flex flex-col gap-4'}>
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} size={cardSize} />
                ))}
              </div>

              <div className="mt-8">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:w-72 flex-shrink-0">
          <div className="card p-5 sticky top-24">
            <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-4">Tags</h3>
            {tags.length === 0 && untaggedCount === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">No tags yet</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {(showAllTags ? tags : tags.slice(0, INITIAL_TAGS_SHOWN)).map((tag) => (
                    <Link
                      key={tag.name}
                      to={`/tag/${encodeURIComponent(tag.name)}`}
                      className={`badge transition-colors ${
                        currentTag === tag.name
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      #{tag.name}
                    </Link>
                  ))}
                  {showAllTags && untaggedCount > 0 && (
                    <button
                      onClick={() => handleTagClick('__untagged__')}
                      className={`badge cursor-pointer transition-colors ${
                        currentTag === '__untagged__'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      Untagged
                    </button>
                  )}
                </div>
                {(tags.length > INITIAL_TAGS_SHOWN || untaggedCount > 0) && (
                  <button
                    onClick={() => setShowAllTags(!showAllTags)}
                    className="mt-3 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
                  >
                    {showAllTags ? (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                        Show less
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        Show all ({tags.length + (untaggedCount > 0 ? 1 : 0)})
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </aside>
      </div>
      </div>
    </>
  );
}
